import type { Task } from '@/common/api/models/task';
import type { Module } from '@/student/types';

export type GradeReportScenarioId =
  | 'essay-classic'
  | 'oral-exam-only'
  | 'oral-exam-rich'
  | 'oral-exam-sparse'
  | 'mcq-delayed'
  | 'mixed'
  | 'minimal';

export interface GradeReportScenario {
  id: GradeReportScenarioId;
  labelKey: string;
  descriptionKey: string;
  module: Module;
  tasks: Task[];
}

export const MODULE_ID = 'playground-module';

export const baseModule = (overrides: Partial<Module> = {}): Module => ({
  id: MODULE_ID,
  title: 'Research Methods Module',
  state: { studentWorkflowState: 'graded' },
  grading: {
    grade: 2.3,
    comment: '<p>Strong work overall. You demonstrated solid understanding across most areas, with room to deepen analytical reflection on transfer tasks.</p>',
  },
  ...overrides,
} as Module);

export const essayEval = (
  levels: Record<string, 'very_good' | 'good' | 'satisfactory' | 'needs_improvement'>,
  comments: Record<string, string> = {},
) => Object.fromEntries(
  Object.entries(levels).map(([dim, level]) => [dim, { level, comment: comments[dim] ?? '' }]),
);

export const transferTask = (
  id: string,
  title: string,
  level: number,
  options: {
    feedbackSentence?: string;
    evaluation?: ReturnType<typeof essayEval>;
  } = {},
): Task => ({
  id,
  title,
  moduleId: MODULE_ID,
  type: 'essay',
  level,
  sortIndex: level,
  gradingSystem: 'full_evaluation',
  description: `<p>${title}: transfer task with full dimension evaluation.</p>`,
  submission: {
    state: 'submitted',
    text: '<p>Student submission text for the transfer task. Lorem ipsum dolor sit amet.</p>',
    feedback: {
      text: options.feedbackSentence
        ? `<p>${options.feedbackSentence}</p>`
        : `<p>Feedback on ${title}: clear structure and good use of sources.</p>`,
    },
    evaluation: options.evaluation ?? essayEval(
      {
        formal: 'very_good',
        description: 'good',
        analysis: 'good',
        application: 'satisfactory',
      },
      { description: 'Good scientific description with minor gaps.' },
    ),
  },
} as unknown as Task);

export const regularTask = (
  id: string,
  title: string,
  level: number,
  options: {
    feedbackSentence?: string;
    evaluation?: ReturnType<typeof essayEval>;
  } = {},
): Task => ({
  id,
  title,
  moduleId: MODULE_ID,
  type: 'essay',
  level,
  sortIndex: level + 10,
  gradingSystem: 'pass_fail',
  description: `<p>${title}: regular activity task.</p>`,
  submission: {
    state: 'submitted',
    text: '<p>Short activity response.</p>',
    feedback: {
      text: options.feedbackSentence
        ? `<p>${options.feedbackSentence}</p>`
        : `<p>Feedback on ${title}: meets expectations.</p>`,
    },
    evaluation: options.evaluation ?? essayEval(
      { formal: 'good', description: 'satisfactory', analysis: 'needs_improvement' },
    ),
  },
} as unknown as Task);

/** Two transfer and two regular tasks with distinct evaluations and feedback for approval lab. */
export function buildWrittenApprovalTasks(): Task[] {
  return [
    transferTask('transfer-1', 'Transfer Task: Case Analysis', 2, {
      feedbackSentence: 'Case analysis feedback: your evidence chain is clear but thin in section two.',
      evaluation: essayEval(
        {
          formal: 'very_good', description: 'good', analysis: 'good', application: 'satisfactory',
        },
        { analysis: 'Analysis dimension: strong causal framing.' },
      ),
    }),
    transferTask('transfer-2', 'Transfer Task: Literature Synthesis', 3, {
      feedbackSentence: 'Literature synthesis feedback: sources are well chosen; synthesis needs sharper contrast.',
      evaluation: essayEval(
        {
          formal: 'good', description: 'very_good', analysis: 'satisfactory', application: 'good',
        },
        { description: 'Description dimension: excellent coverage of prior work.' },
      ),
    }),
    regularTask('regular-1', 'Activity: Weekly Reflection', 1, {
      feedbackSentence: 'Weekly reflection feedback: honest self-critique with concrete next steps.',
      evaluation: essayEval(
        { formal: 'good', description: 'satisfactory', analysis: 'needs_improvement' },
        { formal: 'Formal dimension: clean structure throughout.' },
      ),
    }),
    regularTask('regular-2', 'Activity: Peer Discussion', 2, {
      feedbackSentence: 'Peer discussion feedback: you engaged peers well and tied comments to the rubric.',
      evaluation: essayEval(
        { formal: 'satisfactory', description: 'good', analysis: 'good' },
        { application: 'Application dimension: practical examples landed well.' },
      ),
    }),
  ];
}

export const ORAL_QUESTIONS = [
  { questionId: 'q1', question: 'Describe your research design and justify your methodological choices.' },
  { questionId: 'q2', question: 'How do you ensure validity and reliability in qualitative data collection?' },
  { questionId: 'q3', question: 'Explain how you would apply your findings to a real-world business context.' },
  { questionId: 'q4', question: 'What ethical considerations did you address in your study?' },
  { questionId: 'q5', question: 'Critically reflect on limitations of your approach.' },
  { questionId: 'q6', question: 'How would you extend this research in a follow-up study?' },
];

const ORAL_DIMS = ['content', 'depth', 'application', 'communication', 'professionalism'] as const;

export const dimGrades = (
  levels: Partial<Record<(typeof ORAL_DIMS)[number], string>>,
) => Object.fromEntries(
  Object.entries(levels).map(([dim, level]) => [dim, { level }]),
);

export const oralExamTask = (
  subEvaluations: Array<{ id: string; dimGrades: ReturnType<typeof dimGrades>; questionFeedback?: string }>,
  options: {
    overallFeedback?: string;
    lecturerComment?: string;
    questionPool?: typeof ORAL_QUESTIONS;
  } = {},
): Task => ({
  id: 'oral-exam-task',
  title: 'Module Oral Examination',
  moduleId: MODULE_ID,
  type: 'oral_exam',
  level: 1,
  sortIndex: 0,
  gradingSystem: 'full_evaluation',
  isDeliveredAsEvent: true,
  eventConfig: { eventKindId: 'oral_exam_20' },
  oralExamConfig: { questionPool: options.questionPool ?? ORAL_QUESTIONS },
  submission: {
    state: 'submitted',
    text: '<p>Oral exam transcript would appear here.</p>',
    feedback: options.lecturerComment
      ? { text: options.lecturerComment }
      : undefined,
    oralExamSession: {
      selectedQuestionIds: subEvaluations.map((row) => row.id),
      filledQuestions: Object.fromEntries(
        (options.questionPool ?? ORAL_QUESTIONS)
          .filter((question) => subEvaluations.some((row) => row.id === question.questionId))
          .map((question) => [question.questionId, question.question]),
      ),
    },
    oralExamEvaluation: {
      subEvaluations,
      overallFeedback: options.overallFeedback
        ? { text: options.overallFeedback }
        : undefined,
      promptVersion: 'oral_exam_v1',
    },
  },
} as unknown as Task);

/** Six-question oral exam with per-question feedback for approval lab. */
export function buildOralPassTask(): Task {
  return oralExamTask(
    ORAL_QUESTIONS.map((question, index) => ({
      id: question.questionId,
      dimGrades: dimGrades({
        content: index % 2 === 0 ? 'very_good' : 'good',
        depth: index < 4 ? 'good' : 'satisfactory',
        application: 'good',
        communication: 'very_good',
        professionalism: 'good',
      }),
      questionFeedback: `<p>Question ${question.questionId} feedback: distinct examiner note for this answer.</p>`,
    })),
    {
      overallFeedback: '<p>Overall assessment: strong performance across all six questions with clear communication.</p>',
    },
  );
}

export const mcqTask = (): Task => ({
  id: 'mcq-task',
  title: 'Proctored Knowledge Check',
  moduleId: MODULE_ID,
  type: 'multiple_choice',
  level: 1,
  sortIndex: 5,
  mcqConfig: { showImmediateFeedback: false },
  submission: {
    state: 'submitted',
    mcqResult: {
      score: 18,
      maxScore: 20,
      passed: true,
      attempts: 1,
    },
  },
} as unknown as Task);

export const GRADE_REPORT_SCENARIOS: GradeReportScenario[] = [
  {
    id: 'essay-classic',
    labelKey: 'grade.playground.scenarios.essayClassic.label',
    descriptionKey: 'grade.playground.scenarios.essayClassic.description',
    module: baseModule({ grading: { grade: 1.7, comment: '<p>Consistent quality across transfer and regular tasks.</p>' } }),
    tasks: [
      transferTask('transfer-1', 'Transfer Task: Case Analysis', 2),
      regularTask('regular-1', 'Activity: Weekly Reflection', 1),
    ],
  },
  {
    id: 'oral-exam-only',
    labelKey: 'grade.playground.scenarios.oralExamOnly.label',
    descriptionKey: 'grade.playground.scenarios.oralExamOnly.description',
    module: baseModule({ grading: { grade: 2.0, comment: '<p>Oral examination determines the module grade.</p>' } }),
    tasks: [
      oralExamTask(
        [
          {
            id: 'q1',
            dimGrades: dimGrades({
              content: 'good',
              depth: 'very_good',
              application: 'good',
              communication: 'good',
              professionalism: 'satisfactory',
            }),
            questionFeedback: '<p>Strong opening with clear research framing.</p>',
          },
          {
            id: 'q2',
            dimGrades: dimGrades({
              content: 'satisfactory',
              depth: 'good',
              application: 'satisfactory',
              communication: 'good',
              professionalism: 'good',
            }),
            questionFeedback: '<p>Valid points on reliability; could go deeper on triangulation.</p>',
          },
          {
            id: 'q3',
            dimGrades: dimGrades({
              content: 'good',
              depth: 'good',
              application: 'very_good',
              communication: 'satisfactory',
              professionalism: 'good',
            }),
          },
        ],
        { overallFeedback: '<p>The candidate demonstrated solid mastery of research methods with particularly strong application skills. Communication was clear throughout; depth could be improved on validity questions.</p>' },
      ),
    ],
  },
  {
    id: 'oral-exam-rich',
    labelKey: 'grade.playground.scenarios.oralExamRich.label',
    descriptionKey: 'grade.playground.scenarios.oralExamRich.description',
    module: baseModule({ grading: { grade: 1.3, comment: '<p>Excellent oral examination performance.</p>' } }),
    tasks: [
      oralExamTask(
        ORAL_QUESTIONS.map((question, index) => ({
          id: question.questionId,
          dimGrades: dimGrades({
            content: index % 2 === 0 ? 'very_good' : 'good',
            depth: index < 4 ? 'good' : 'satisfactory',
            application: 'good',
            communication: 'very_good',
            professionalism: 'good',
          }),
          questionFeedback: `<p>Detailed feedback for ${question.questionId}: comprehensive answer with good examples.</p>`,
        })),
        {
          overallFeedback: '<p>Outstanding performance across all six questions. The candidate showed deep methodological knowledge, clear communication, and strong ability to connect theory to practice. Minor improvement possible in critical self-reflection on limitations.</p>',
          lecturerComment: '<p>Confirmed grade after review. Exceptional exam.</p>',
        },
      ),
    ],
  },
  {
    id: 'oral-exam-sparse',
    labelKey: 'grade.playground.scenarios.oralExamSparse.label',
    descriptionKey: 'grade.playground.scenarios.oralExamSparse.description',
    module: baseModule({ grading: { grade: 3.7, comment: '<p>Module grade submitted; oral exam evaluation is partial.</p>' } }),
    tasks: [
      oralExamTask(
        [{ id: 'q1', dimGrades: dimGrades({ content: 'satisfactory' }) }],
        { lecturerComment: '<p>Autograder did not run yet. Lecturer left interim comment only.</p>' },
      ),
    ],
  },
  {
    id: 'mcq-delayed',
    labelKey: 'grade.playground.scenarios.mcqDelayed.label',
    descriptionKey: 'grade.playground.scenarios.mcqDelayed.description',
    module: baseModule({ grading: { grade: 2.0, comment: '<p>Proctored exam module.</p>' } }),
    tasks: [mcqTask()],
  },
  {
    id: 'mixed',
    labelKey: 'grade.playground.scenarios.mixed.label',
    descriptionKey: 'grade.playground.scenarios.mixed.description',
    module: baseModule(),
    tasks: [
      oralExamTask(
        [
          {
            id: 'q1',
            dimGrades: dimGrades({ content: 'good', depth: 'good', communication: 'good' }),
            questionFeedback: '<p>Solid answer.</p>',
          },
          { id: 'q2', dimGrades: dimGrades({ content: 'satisfactory', depth: 'satisfactory' }) },
        ],
        { overallFeedback: '<p>Oral exam was satisfactory overall.</p>' },
      ),
      transferTask('transfer-mixed', 'Transfer Task: Literature Review', 2),
      regularTask('regular-mixed', 'Activity: Peer Discussion', 1),
    ],
  },
  {
    id: 'minimal',
    labelKey: 'grade.playground.scenarios.minimal.label',
    descriptionKey: 'grade.playground.scenarios.minimal.description',
    module: baseModule({
      grading: {
        grade: 2.7,
        comment: '<p>Module graded. No per-task evaluations available yet.</p>',
      },
    }),
    tasks: [],
  },
];

export const DEFAULT_SCENARIO_ID: GradeReportScenarioId = 'oral-exam-rich';
